import { Dialog } from '@base-ui/react/dialog'
import type { FC } from 'react'
import styles from './drawer.module.css'

const anchorMap = {
  left: styles.left,
  right: styles.right,
  top: styles.top,
  bottom: styles.bottom,
} as const

export interface DrawerProps {
  open: boolean
  onClose?: () => void
  anchor?: 'left' | 'right' | 'top' | 'bottom'
  variant?: 'temporary' | 'permanent' | 'persistent'
  /** Width (for left/right) or height (for top/bottom) */
  width?: number | string
  className?: string
  style?: React.CSSProperties
  children?: React.ReactNode
  /** Accessible name of the drawer (Base UI renders it as a `dialog`) */
  'aria-label'?: string
  /** What takes focus on open, when not the drawer's first focusable (e.g. a picker's selected option) */
  initialFocus?: () => HTMLElement | null
}

export const Drawer: FC<DrawerProps> = ({
  open,
  onClose,
  anchor = 'left',
  variant = 'temporary',
  width = 250,
  className,
  children,
  style,
  'aria-label': ariaLabel,
  initialFocus,
}) => {
  const isHorizontal = anchor === 'left' || anchor === 'right'
  const sizeStyle = isHorizontal ? { width } : { height: width }

  const drawerClasses = [styles.drawer, anchorMap[anchor], className].filter(Boolean).join(' ')

  if (variant === 'permanent') {
    return (
      <div className={drawerClasses} style={{ ...sizeStyle, ...style }}>
        {children}
      </div>
    )
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={o => {
        if (!o) {
          onClose?.()
        }
      }}
    >
      <Dialog.Portal>
        {variant === 'temporary' ? <Dialog.Backdrop className={styles.backdrop} /> : null}
        <Dialog.Popup
          className={drawerClasses}
          style={{ ...sizeStyle, ...style }}
          aria-label={ariaLabel}
          initialFocus={initialFocus ? () => initialFocus() ?? true : undefined}
        >
          {children}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
